import "./index.css";
// Polyfill Buffer for browser-compatible use of ExcelJS
import { Buffer } from "buffer";

if (typeof window !== "undefined" && !(window as any).Buffer) {
  (window as any).Buffer = Buffer;
}
import * as RadixTooltip from "@radix-ui/react-tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Toaster } from "sonner";

// Broadcast API config to the recorder extension on every page load.
// This ensures the extension always connects to the same server as the web app,
// regardless of environment (local, dev, prod). No separate config needed in the extension.
window.postMessage(
  {
    from: "autopilotqa_serv",
    type: "AUTOPILOT_CONFIG_UPDATE",
    serverUrl: import.meta.env.VITE_SERVERURL,
    domainUrl: import.meta.env.VITE_BASEURL,
  },
  "*",
);

if (import.meta.env.DEV && import.meta.env.VITE_ENABLE_LOCATOR === "true") {
  import("@locator/runtime").then(({ default: setupLocatorUI }) => {
    setupLocatorUI();
  });
}
import { PluginModal } from "./common/components/PluginModal";
import { TourProvider } from "./common/components/tour";
import { AuthProvider } from "./common/hooks/useAuth";
import { PluginProvider } from "./common/hooks/usePlugin";
import { ToastProvider } from "./common/hooks/useToast";
import AppRouter from "./router";
import { ThemeProvider, useTheme } from "./themes/ThemeProvider";
import "./i18n";

function ThemedToaster() {
  const { resolvedTheme } = useTheme();
  return (
    <Toaster
      position="top-center"
      closeButton
      theme={resolvedTheme}
      toastOptions={{
        className:
          "bg-card text-foreground border-border shadow-md px-3 py-2 gap-1.5",
      }}
    />
  );
}

// CREATE QueryClient instance with global defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false, // don't refetch when user switches tabs
      // Never retry 402/401/403 — these are definitive; retrying just spams events
      retry: (failureCount, error: unknown) => {
        const status = (error as any)?.response?.status;
        if (status === 402 || status === 401 || status === 403) return false;
        return failureCount < 1;
      },
    },
    mutations: {
      // Deliberately do NOT auto-retry mutations: many are non-idempotent (create,
      // copy, import) and a blind retry on a transient/network error risks a
      // duplicate write. Transient hangs are bounded by the httpClient timeout;
      // callers that are safe to retry can opt in per-mutation.
      retry: 0,
    },
  },
});

let root: ReturnType<typeof createRoot>;
if (import.meta.hot) {
  import.meta.hot.data.root ??= createRoot(document.getElementById("root")!);
  root = import.meta.hot.data.root as ReturnType<typeof createRoot>;
} else {
  root = createRoot(document.getElementById("root")!);
}
root.render(
  <StrictMode>
    <RadixTooltip.Provider delayDuration={300} skipDelayDuration={100}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <PluginProvider>
              <ToastProvider>
                <TourProvider>
                  <AppRouter />
                </TourProvider>
                <PluginModal />
              </ToastProvider>
            </PluginProvider>
          </AuthProvider>
          <ThemedToaster />
        </ThemeProvider>
      </QueryClientProvider>
    </RadixTooltip.Provider>
  </StrictMode>,
);
